import React, { BaseSyntheticEvent, FC, useState } from "react";
import { CloseButton, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { renameFolder } from "../../../graphql/state";
import { useReactiveVar } from "@apollo/client";
import { Formik } from "formik";
import { Form, useLocation, useNavigate } from "react-router-dom";
import { useUserQuery } from "../../../graphql/menu.hooks";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import { CustomToast } from "../../inputs/toast";
import {
  useFolderEditMutation,
  useFolderShowQuery,
} from "../../../graphql/folder.hooks";
import { renameFolderSchema } from "./validation/renameFolderSchema";
import { StyledInput } from "../../inputs/styledInput";
import { usePagesByDomainQuery } from "../../../graphql/projects.hooks";

type Props = {
  show: boolean;
  onCancel: () => void;
};

export const RenameFolderModal: FC<Props> = ({ show, onCancel }) => {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();
  const navigate = useNavigate();
  const renameFolderData = useReactiveVar(renameFolder);

  const { data: user } = useUserQuery();
  const { data: folder, loading: folderLoading } = useFolderShowQuery({
    variables: { folderId: +renameFolderData.folderId },
  });
  const { refetch: refetchFolders } = usePagesByDomainQuery({
    variables: { domainId: folder?.folderShow?.folder?.domain?.id },
  });
  const [editFolder, { loading: editLoading }] = useFolderEditMutation();

  const [isShowToast, setIsShowToast] = useState<boolean>(false);

  const location = useLocation();
  const sharedValue = location.state?.shared;
  const accessType = folder?.folderShow?.folder.userAccesses?.find(
    (userAccess) => userAccess?.user?.email === user?.user.email
  );

  const closeModal = () => {
    onCancel();
  };

  const closeByBackground = (e: BaseSyntheticEvent) => {
    e.stopPropagation();
    if (e.target.id === "confirmModal") closeModal();
  };

  const initialValues = {
    title: folder?.folderShow.folder.title || "",
    url: folder?.folderShow.folder.url || "",
  };

  const parentFolder = folder?.folderShow?.folder?.parentFolder;

  const tryRenameFolder = async (values: any) => {
    const editedFolder = {
      folderId: +renameFolderData.folderId,
      title: values.title,
      url: values.url,
      domainId: folder?.folderShow.folder.domain.id,
    };

    const { data } = await editFolder({ variables: editedFolder });

    if (data?.folderEdit.id) {
      refetchFolders();
      closeModal();
      if (sharedValue || accessType) {
        navigate(`${lng}/shared`);
        return;
      }

      navigate(
        `${lng}/projects/${folder?.folderShow.folder.domain.id}${parentFolder ? `/folder/${parentFolder?.id}` : ""}`
      );
    }
  };

  if (folderLoading) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <Spinner animation="border" role="status"></Spinner>
      </div>
    );
  }

  return (
    <div
      id="confirmModal"
      className={`modal ${show ? "show d-flex" : ""} align-items-center justify-content-center`}
      style={{
        display: show ? "block" : "none",
        background: "rgb(0, 0, 0, 0.6)",
      }}
      onClick={(e) => closeByBackground(e)}
    >
      <div className="modal-dialog">
        <div className="renameModal modal-content text-break">
          <div className="modal-header border-0 d-flex justify-content-between align-items-baseline">
            <div className="d-flex flex-column">
              <h5 className="modal-title fw-700 fs-20">
                {t("folder.renameFolder")}
              </h5>
            </div>
            <CloseButton
              onClick={closeModal}
              style={{ height: "16px", width: "16px", opacity: "1" }}
            />
          </div>
          <div className="modal-body border-0 d-flex flex-column justify-content-start p-3 pt-0">
            <p className="text-gray mb-3">
              {t("modal.You are about to rename the folder")}
              <span className="fw-bold text-dark">
                {" "}
                {folder?.folderShow.folder.title}{" "}
              </span>
              <p>{t("modal.Please enter a new name and URL.")}</p>
            </p>
            <Formik
              initialValues={initialValues}
              validationSchema={renameFolderSchema(t)}
              onSubmit={tryRenameFolder}
              enableReinitialize
            >
              {({ handleSubmit, touched, errors, values }) => (
                <Form className="g-4 d-flex justify-content-center align-items-center bg-white rounded">
                  <div className="col-12">
                    <div className="form-floating mb-3">
                      <StyledInput
                        name="title"
                        label={t("folder.folderName")}
                        error={!!(touched.title && errors.title)}
                      />
                    </div>
                    <div className="form-floating mb-3">
                      <StyledInput
                        name="url"
                        label={t("folder.urlName")}
                        error={!!(touched.url && errors.url)}
                      />
                    </div>
                    <div className="mt-3 d-flex align-items-center justify-content-between">
                      <button
                        type="button"
                        className="buttomCol btn btn-outline-dark fw-normal dark-hover col-md-4 me-3 me-md-0"
                        onClick={closeModal}
                      >
                        {t("profile.cancel")}
                      </button>
                      <button
                        type="button"
                        disabled={
                          editLoading ||
                          (values.title === folder?.folderShow.folder.title &&
                            values.url === folder?.folderShow.folder.url)
                        }
                        onClick={() => handleSubmit()}
                        className={`btn btn-primary text-white buttomCol col-md-4 transition ${values.title === folder?.folderShow.folder.title && values.url === folder?.folderShow.folder.url && "opacity-50 disabled-block"}`}
                      >
                        {t("folder.rename")}
                        {editLoading && (
                          <Spinner
                            animation="border"
                            role="status"
                            className="ms-2"
                          >
                            <span className="visually-hidden">
                              {t("profile.loading")}...
                            </span>
                          </Spinner>
                        )}
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
            <CustomToast
              isShow={isShowToast}
              setShow={setIsShowToast}
              background="bg-danger"
              message={`${t("page.uniqueUrl")}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
